/**
 * @file useReceiptLabels.ts
 * @description Этикетки проведённой приёмки: блок «5 из 8 поз.», журнал
 *   печати и перепечатка (phase19.22, W17-5a, W17-9c; ui-design §1).
 *
 * Статус и журнал — с сервера и после каждой печати перечитываются (один
 * источник правды): признак «напечатаны» ставит сервер в той же транзакции,
 * что и задача печати. У черновика ничего не грузим — печать только после
 * проведения, а сервер ответил бы 409. «Скрыть файл» записи журнала —
 * `useLabelJobHide` (phase19.22 шаг 5).
 */

import { useCallback, useEffect, useState } from "react";

import { resultTitle } from "./label-texts";
import { useLabelJobHide } from "./useLabelJobHide";
import { useNotify } from "../../ui/NotifyProvider";
import {
  labelsApi,
  type LabelPrintJobDto,
  type ReceiptLabelStatusDto,
} from "../../../lib/api-labels";
import { wmsErrorText } from "../../../lib/wms-error-text";

export function useReceiptLabels(receiptId: string | undefined, committed: boolean) {
  const notify = useNotify();
  const [status, setStatus] = useState<ReceiptLabelStatusDto | null>(null);
  const [jobs, setJobs] = useState<LabelPrintJobDto[]>([]);
  const [jobsLoading, setJobsLoading] = useState(false);
  const [reprintingId, setReprintingId] = useState<string | null>(null);
  const [version, setVersion] = useState(0);

  const reload = useCallback(() => setVersion((v) => v + 1), []);
  const { hidingId, hideFile } = useLabelJobHide(reload);

  useEffect(() => {
    if (!receiptId || !committed) {
      setStatus(null);
      return;
    }
    let cancelled = false;
    labelsApi
      .receiptStatus(receiptId)
      .then((data) => {
        if (!cancelled) setStatus(data);
      })
      .catch(() => {
        // Блок этикеток — не главное на карточке: без статуса документ читается.
        if (!cancelled) setStatus(null);
      });
    return () => {
      cancelled = true;
    };
  }, [receiptId, committed, version]);

  /** Журнал печати: секция под «Движениями» на `≥lg` и шторка на телефоне. */
  const loadJobs = useCallback(async () => {
    if (!receiptId) return;
    setJobsLoading(true);
    try {
      setJobs(await labelsApi.jobs({ receiptId }));
    } catch (err) {
      notify.error(wmsErrorText(err, "Не удалось загрузить историю печати"));
    } finally {
      setJobsLoading(false);
    }
  }, [receiptId, notify]);

  // Журнал живёт рядом со статусом: печать меняет оба, перечитываем вместе.
  useEffect(() => {
    if (receiptId && committed) void loadJobs();
  }, [receiptId, committed, version, loadJobs]);

  /** Перепечатать задачу с текущими данными партий (W17-9c). */
  const reprint = useCallback(
    async (job: LabelPrintJobDto) => {
      setReprintingId(job.id);
      try {
        const res = await labelsApi.reprint(job.id);
        const file = res.files[0];
        notify.success(resultTitle(res.sheetsCount, res.labelsCount), {
          action: file
            ? { label: "Открыть", onClick: () => window.open(file.url, "_blank", "noopener") }
            : undefined,
        });
        reload();
      } catch (err) {
        notify.error(wmsErrorText(err, "Не удалось перепечатать этикетки"));
      } finally {
        setReprintingId(null);
      }
    },
    [notify, reload],
  );

  return { status, jobs, jobsLoading, reprintingId, reload, loadJobs, reprint, hidingId, hideFile };
}

export type ReceiptLabels = ReturnType<typeof useReceiptLabels>;
